'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { roundTo } from '@/utils';
import { BREAKPOINTS } from '@/constants';

import Demo, { DemoSliderControl } from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import {
  generateSnippet,
  INITIAL_WIDTH,
} from './NonScalableSVGDemo.helpers';

interface Props {
  variant?: 'default' | 'fixed' | 'viewbox';
  codeOutput: React.ReactNode;
}

function NonScalableSVGDemo({
  variant = 'default',
  codeOutput,
}: Props) {
  return (
    <OuterWrapper>
      <Demo
        outputWrapper={null}
        initialValues={{
          width: INITIAL_WIDTH,
        }}
        customGrid={{
          gridTemplateColumns: 'min(400px, 100%)',
          justifyContent: 'center',
        }}
        controlPosition="top"
        controls={[
          <DemoSliderControl
            key="width"
            id="width"
            label="Width"
            min={50}
            max={100}
            previewRenderer={(value) => {
              return `${value}%`;
            }}
          />,
        ]}
      >
        {({ width }) => {
          const widthInPixels = Math.round(300 * (width / 100));
          const widthRatio = variant === 'fixed' ? width / 100 : 1;

          return (
            <Row>
              <CodeWrapper>
                <CodeSnippetProvider
                  lang="html"
                  code={generateSnippet(widthInPixels, variant)}
                >
                  {codeOutput}
                </CodeSnippetProvider>
              </CodeWrapper>
              <SvgWrapper data-variant={variant}>
                <Svg
                  width={widthInPixels}
                  height={
                    variant !== 'viewbox'
                      ? Math.round(220 * widthRatio)
                      : undefined
                  }
                  viewBox={
                    variant === 'viewbox' ? '0 0 300 220' : undefined
                  }
                >
                  <circle
                    cx={roundTo(150 * widthRatio, 1)}
                    cy={roundTo(110 * widthRatio, 1)}
                    r={roundTo(60 * widthRatio, 1)}
                    fill="none"
                    strokeWidth={roundTo(10 * widthRatio, 1)}
                  />
                </Svg>
              </SvgWrapper>
            </Row>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  margin-inline: -24px;
  container-type: inline-size;

  @media ${BREAKPOINTS.mdAndSmaller} {
    margin-inline: 0;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  @container (max-width: 44.5rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

const CodeWrapper = styled.div`
  --inline-padding-override: 16px;
  --block-padding-override: 16px;
  --code-font-size: 0.875rem;
  --code-line-height: 1.25;

  flex: 1;
  min-width: 12.5rem;
  outline: 1px solid var(--color-gray-400);
  border-radius: 2px;
  white-space: pre;
  overflow: hidden;

  @container (max-width: 44.5rem) {
    width: 450px;
    max-width: 100%;
  }
  @container (max-width: 30rem) {
    white-space: pre-wrap;
  }
`;

const SvgWrapper = styled.div`
  width: 100%;
  max-width: 314px;
  border: 2px dashed var(--color-gray-300);
  padding: 5px;
  align-self: flex-start;

  @container (max-width: 44.5rem) {
    align-self: center;
  }
`;

const Svg = styled.svg`
  display: block;
  outline: 1px solid var(--color-gray-300);

  circle {
    stroke: hsl(45deg 100% 50%);
    stroke: oklch(0.83 0.2 77.44);
  }
`;

export default NonScalableSVGDemo;
